import { useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Download, UploadCloud, RotateCcw, Sparkles } from "lucide-react";
import { api, API, errMsg } from "@/lib/api";
import { PageHeader, Panel, Segmented } from "@/components/dash";
import { DataTable } from "@/components/DataTable";
import { Markdown } from "@/components/AIInsights";
import { inputCls, btnGhost } from "@/components/admin/AdminLayout";

export default function AdminData() {
  const qc = useQueryClient();
  const ref = useRef();
  const [module, setModule] = useState("demand");
  const [mode, setMode] = useState("products");
  const [override, setOverride] = useState("");
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const { data: metas = [], refetch } = useQuery({ queryKey: ["datasets"], queryFn: async () => (await api.get("/admin/datasets")).data });
  const { data: log = [], refetch: relog } = useQuery({ queryKey: ["upload-log"], queryFn: async () => (await api.get("/admin/upload-log")).data });

  const send = async (f, commit) => {
    const fd = new FormData();
    fd.append("file", f);
    fd.append("product_override", override);
    if (commit) fd.append("ai_notes", preview?.validation || "");
    return (await api.post(`/admin/upload/${module}?commit=${commit}&mode=${mode}`, fd)).data;
  };
  const pick = async (f) => {
    if (!f) return;
    setFile(f); setPreview(null); setBusy(true);
    try { setPreview(await send(f, false)); } catch (e) { toast.error(errMsg(e)); setFile(null); }
    setBusy(false);
  };
  const apply = async () => {
    setBusy(true);
    try {
      const r = await send(file, true);
      toast.success(`Published ${r.valid_rows} rows · ${r.products.join(", ")}`);
      qc.invalidateQueries(); refetch(); relog(); setFile(null); setPreview(null);
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  const reset = async (m) => { await api.post(`/admin/data/${m}/reset`); qc.invalidateQueries(); refetch(); toast.success("Sample data restored"); };
  const cols = preview?.preview?.[0] ? Object.keys(preview.preview[0]).filter((k) => typeof preview.preview[0][k] !== "object") : [];

  return (
    <div className="space-y-6">
      <PageHeader eyebrow="CMS · Dashboard Data" title="Excel Data Uploads" desc="Upload Excel in the template format. AI validates the numbers and flags anomalies — your figures are always published as provided once you approve." />
      <div className="grid md:grid-cols-2 xl:grid-cols-5 gap-3">
        {metas.map((m) => (
          <div key={m.module} data-testid={`admin-data-card-${m.module}`} onClick={() => { setModule(m.module); setPreview(null); setFile(null); }}
            className={`cursor-pointer rounded-lg border p-4 ${module === m.module ? "border-[#1074BA] bg-[#1074BA]/10" : "border-slate-800 bg-[#0F172A]/80 hover:border-slate-600"}`}>
            <p className="font-semibold text-sm">{m.label}</p>
            <p className="text-xs text-slate-400 mt-2 font-mono">{m.rows.toLocaleString()} rows · {m.products} products</p>
            <p className="text-[11px] text-slate-500 truncate mt-1">{m.source} · {m.updated_at?.slice(0, 10)}</p>
            <div className="flex gap-1.5 mt-3" onClick={(e) => e.stopPropagation()}>
              <a href={`${API}/templates/${m.module}`} className={`${btnGhost} flex items-center gap-1`} data-testid={`admin-template-${m.module}`}><Download className="h-3 w-3" />Template</a>
              <button onClick={() => reset(m.module)} className={`${btnGhost} flex items-center gap-1`} data-testid={`admin-reset-${m.module}`}><RotateCcw className="h-3 w-3" />Sample</button>
            </div>
          </div>
        ))}
      </div>
      <Panel title={`Upload · ${metas.find((m) => m.module === module)?.label || module}`} sub=".xlsx · uploaded files are archived in secure storage"
        right={<Segmented testId="admin-upload-mode" value={mode} onChange={setMode} options={[{ value: "products", label: "Replace products in file" }, { value: "all", label: "Replace all" }]} />}>
        <input className={`${inputCls} w-full md:w-96 mb-4`} placeholder="Optional: product name override (e.g. Aklyl Polyglucoside (APG))" value={override} onChange={(e) => setOverride(e.target.value)} data-testid="admin-product-override" />
        <div data-testid="admin-upload-dropzone" onClick={() => ref.current.click()} onDragOver={(e) => e.preventDefault()} onDrop={(e) => { e.preventDefault(); pick(e.dataTransfer.files[0]); }}
          className="border-2 border-dashed border-slate-700 hover:border-slate-500 rounded-lg py-10 grid place-items-center text-center cursor-pointer">
          <UploadCloud className="h-8 w-8 text-[#1074BA] mb-2" />
          <p className="text-sm">{file ? file.name : "Drop .xlsx here or click to browse"}</p>
          <p className="text-xs text-slate-500 mt-1">{busy ? "Parsing & running AI validation…" : "Preview + AI validation before publishing"}</p>
          <input ref={ref} type="file" accept=".xlsx,.xlsm" className="hidden" data-testid="admin-upload-input" onChange={(e) => { pick(e.target.files[0]); e.target.value = ""; }} />
        </div>
        {preview && (
          <div className="mt-6 grid lg:grid-cols-[1fr_380px] gap-6" data-testid="admin-upload-preview">
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-3 mb-3">
                <span className="text-sm text-[#03AB9E]">{preview.valid_rows} valid rows</span>
                <span className="text-xs text-slate-400">Products: {preview.products.join(", ")}</span>
                {preview.errors.length > 0 && <span className="text-sm text-red-400">{preview.errors.length} rows skipped</span>}
                <button onClick={apply} disabled={busy || !preview.valid_rows} data-testid="admin-upload-publish" className="ml-auto px-4 py-2 rounded-md bg-[#F58B23] hover:bg-[#D97618] text-slate-950 font-semibold text-sm disabled:opacity-50">{busy ? "Publishing…" : "Approve & publish"}</button>
              </div>
              {preview.errors.length > 0 && <div className="font-mono text-xs text-red-300 bg-red-500/5 border border-red-500/20 rounded p-3 max-h-28 overflow-auto mb-3">{preview.errors.map((e) => <div key={e}>{e}</div>)}</div>}
              <div className="overflow-x-auto rounded-md border border-slate-800">
                <table className="w-full text-xs"><thead className="bg-slate-900"><tr>{cols.map((c) => <th key={c} className="px-3 py-2 text-left font-mono uppercase text-slate-400">{c}</th>)}</tr></thead>
                  <tbody>{preview.preview.map((r, i) => <tr key={i} className="border-t border-slate-800">{cols.map((c) => <td key={c} className="px-3 py-1.5 whitespace-nowrap">{typeof r[c] === "number" ? r[c].toFixed(2) : String(r[c])}</td>)}</tr>)}</tbody></table>
              </div>
            </div>
            <div className="border border-[#03AB9E]/30 rounded-lg p-4 bg-slate-950/40" data-testid="admin-ai-validation">
              <p className="flex items-center gap-2 text-sm font-semibold mb-3"><Sparkles className="h-4 w-4 text-[#03AB9E]" />AI validation</p>
              {preview.validation ? <Markdown text={preview.validation} small /> : <p className="text-xs text-slate-500">Not available.</p>}
            </div>
          </div>
        )}
      </Panel>
      <Panel title="Upload history">
        <DataTable testId="admin-upload-log" rows={log} columns={[{ key: "at", label: "When", render: (r) => r.at.slice(0, 16).replace("T", " ") }, { key: "module", label: "Module" }, { key: "filename", label: "File" },
          { key: "rows", label: "Rows", num: true }, { key: "products", label: "Products", render: (r) => r.products.join(", ") }, { key: "by", label: "By" }]} />
      </Panel>
    </div>
  );
}
